/**
 * SystemCurrenciesSection - Section de gestion des devises systeme
 * 
 * Utilise dans /admin/settings pour gerer:
 * - Liste des devises systeme
 * - Taux de change (1 DEVISE = X MAD)
 * - Symboles
 * - Devise de reference (base)
 * - Mise a jour automatique depuis source externe
 */

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  RefreshCw, DollarSign, Edit, Plus, Check, X, 
  TrendingUp, AlertCircle, Globe, Save, Download, Trash2, Clock
} from 'lucide-react';
import { safeFixed } from '../../utils/formatters';

export default function SystemCurrenciesSection() {
  const [currencies, setCurrencies] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState('MAD');
  const [rateConvention, setRateConvention] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [cronStatus, setCronStatus] = useState(null);
  const [newCurrency, setNewCurrency] = useState({
    code: '',
    label: '',
    symbol: '',
    rate: ''
  });

  const fetchCurrencies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/currencies');
      setCurrencies(res.data.currencies || []);
      setBaseCurrency(res.data.base_currency || 'MAD');
      setRateConvention(res.data.rate_convention || '1 DEVISE = X MAD');
    } catch (error) {
      console.error('Error fetching currencies:', error);
      toast.error('Erreur lors du chargement des devises');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCronStatus = useCallback(async () => {
    try {
      const res = await api.get('/currencies/cron-status');
      setCronStatus(res.data);
    } catch (error) {
      console.error('Error fetching cron status:', error);
    }
  }, []);

  useEffect(() => {
    fetchCurrencies();
    fetchCronStatus();
  }, [fetchCurrencies, fetchCronStatus]);

  // Mise a jour automatique depuis source externe
  const handleRefreshFromExternal = async () => {
    try {
      setRefreshing(true);
      const res = await api.post('/currencies/refresh');
      
      if (res.data.success) {
        toast.success(`Taux mis a jour depuis ${res.data.source}`);
        setLastRefresh({
          source: res.data.source,
          timestamp: res.data.timestamp,
          updated: res.data.updated
        });
        fetchCurrencies();
        fetchCronStatus();
      } else {
        toast.error(res.data.error || 'Echec de la mise a jour');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la mise a jour automatique');
    } finally {
      setRefreshing(false);
    }
  };

  const handleUpdateCurrency = async (code, data) => {
    try {
      setSaving(true);
      await api.put(`/currencies/${code}`, data);
      toast.success('Devise mise a jour');
      fetchCurrencies();
      setEditingCurrency(null);
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCurrency = async (code) => {
    if (code === baseCurrency) {
      toast.error('Impossible de supprimer la devise de base');
      return;
    }
    
    if (!window.confirm(`Supprimer la devise ${code} ?`)) return;
    
    try {
      setSaving(true);
      await api.delete(`/currencies/${code}`);
      toast.success('Devise supprimee');
      fetchCurrencies();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCurrency = async () => {
    if (!newCurrency.code || !newCurrency.label || !newCurrency.symbol || !newCurrency.rate) {
      toast.error('Tous les champs sont requis');
      return;
    }

    try {
      setSaving(true);
      await api.post('/currencies', {
        code: newCurrency.code.toUpperCase(),
        label: newCurrency.label,
        symbol: newCurrency.symbol,
        rate: parseFloat(newCurrency.rate)
      });
      toast.success('Devise ajoutee');
      setShowAddDialog(false);
      setNewCurrency({ code: '', label: '', symbol: '', rate: '' });
      fetchCurrencies();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la creation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="system-currencies-section">
        <CardContent className="py-12">
          <div className="flex justify-center">
            <RefreshCw className="w-8 h-8 animate-spin text-violet-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const baseCurrencyData = currencies.find(c => c.is_base);
  const otherCurrencies = currencies.filter(c => !c.is_base);

  return (
    <div className="space-y-6" data-testid="system-currencies-section">
      {/* Header Card */}
      <Card className="border-2 border-emerald-200 bg-emerald-50/50">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 border border-emerald-200 flex items-center justify-center">
                <Globe className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-emerald-800">Devises Systeme</CardTitle>
                <CardDescription>
                  Gerez les devises disponibles pour tous les tenants
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefreshFromExternal}
                disabled={refreshing}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="refresh-external-btn"
              >
                <Download className={`w-4 h-4 mr-2 ${refreshing ? 'animate-bounce' : ''}`} />
                {refreshing ? 'Actualisation...' : 'MAJ depuis source externe'}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchCurrencies}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Recharger
              </Button>
              <Button 
                size="sm" 
                onClick={() => setShowAddDialog(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="add-currency-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Last Refresh Info */}
      {lastRefresh && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="py-3">
            <div className="flex items-center gap-3 text-sm text-blue-800">
              <Check className="w-4 h-4 text-blue-600" />
              <span>
                Derniere MAJ depuis <strong>{lastRefresh.source}</strong> : {lastRefresh.updated?.length || 0} devises mises a jour
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Base Currency */}
      {baseCurrencyData && (
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                Devise de reference
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-green-200">
              <div className="flex items-center gap-4">
                <span className="text-3xl font-bold">{baseCurrencyData.symbol}</span>
                <div>
                  <p className="font-semibold text-lg">{baseCurrencyData.label}</p>
                  <p className="text-sm text-muted-foreground font-mono">{baseCurrencyData.code}</p>
                </div>
              </div>
              <div className="text-right">
                <Badge className="bg-green-500 text-white">
                  <Check className="w-3 h-3 mr-1" />
                  Taux = 1.0000
                </Badge>
                <p className="text-xs text-muted-foreground mt-1">
                  Tous les taux sont exprimes par rapport a cette devise
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Other Currencies */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Taux de change
          </CardTitle>
          <CardDescription>
            Convention : {rateConvention}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {otherCurrencies.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Aucune devise configuree</p>
                <p className="text-sm">Cliquez sur "Ajouter" pour creer une devise</p>
              </div>
            ) : (
              otherCurrencies.map(currency => (
                <CurrencyRow
                  key={currency.code}
                  currency={currency}
                  baseCurrency={baseCurrency}
                  isEditing={editingCurrency === currency.code}
                  onEdit={() => setEditingCurrency(currency.code)}
                  onCancel={() => setEditingCurrency(null)}
                  onSave={(data) => handleUpdateCurrency(currency.code, data)}
                  onDelete={() => handleDeleteCurrency(currency.code)}
                  saving={saving}
                />
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">Fonctionnement des devises</p>
              <ul className="space-y-1 text-blue-700">
                <li>Les devises sont partagees par tous les tenants (riads)</li>
                <li>Chaque tenant choisit sa devise principale dans ses parametres</li>
                <li>Les taux modifies ici sont refletes partout immediatement</li>
                <li><strong>Source externe :</strong> open.er-api.com (gratuit, mise a jour quotidienne)</li>
                <li><strong>Convention :</strong> 1 [DEVISE] = X MAD (Dirham marocain)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cron Status Card */}
      {cronStatus && (
        <Card className="bg-violet-50 border-violet-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-violet-600 mt-0.5" />
              <div className="text-sm text-violet-800 flex-1">
                <p className="font-medium mb-2">Mise a jour automatique</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-violet-700">
                      <strong>Statut :</strong>{' '}
                      {cronStatus.cron?.running ? (
                        <Badge className="bg-green-500 text-white ml-1">Actif</Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-500 ml-1">Inactif</Badge>
                      )}
                    </p>
                    <p className="text-violet-700 mt-1">
                      <strong>Frequence :</strong> Tous les jours a 6h00 (Maroc)
                    </p>
                    <p className="text-violet-700 mt-1">
                      <strong>Expression Cron :</strong> <code className="bg-violet-100 px-1 rounded">{cronStatus.cron?.cronExpression}</code>
                    </p>
                  </div>
                  <div>
                    {cronStatus.cron?.lastRun && (
                      <p className="text-violet-700">
                        <strong>Derniere execution :</strong>{' '}
                        {new Date(cronStatus.cron.lastRun).toLocaleString('fr-FR')}
                      </p>
                    )}
                    {cronStatus.cron?.lastStatus && (
                      <p className="text-violet-700 mt-1">
                        <strong>Resultat :</strong>{' '}
                        {cronStatus.cron.lastStatus.success ? (
                          <span className="text-green-600">{cronStatus.cron.lastStatus.updatedCount} devises mises a jour</span>
                        ) : (
                          <span className="text-red-600">Echec : {cronStatus.cron.lastStatus.error}</span>
                        )}
                      </p>
                    )}
                    {!cronStatus.cron?.lastRun && (
                      <p className="text-violet-600 italic">
                        Aucune execution encore. Prochaine MAJ a 6h00.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Currency Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle devise</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Code (3 lettres)</Label>
                <Input
                  value={newCurrency.code}
                  onChange={(e) => setNewCurrency({ ...newCurrency, code: e.target.value.toUpperCase() })}
                  placeholder="USD"
                  maxLength={3}
                  className="font-mono uppercase"
                  data-testid="new-currency-code"
                />
              </div>
              <div>
                <Label>Symbole</Label>
                <Input
                  value={newCurrency.symbol}
                  onChange={(e) => setNewCurrency({ ...newCurrency, symbol: e.target.value })}
                  placeholder="$"
                  maxLength={3}
                  data-testid="new-currency-symbol"
                />
              </div>
            </div>
            <div>
              <Label>Libelle</Label>
              <Input
                value={newCurrency.label}
                onChange={(e) => setNewCurrency({ ...newCurrency, label: e.target.value })}
                placeholder="Dollar americain"
                data-testid="new-currency-label"
              />
            </div>
            <div>
              <Label>Taux (1 {newCurrency.code || 'DEVISE'} = X {baseCurrency})</Label>
              <Input
                type="number"
                step="0.0001"
                value={newCurrency.rate}
                onChange={(e) => setNewCurrency({ ...newCurrency, rate: e.target.value })}
                placeholder="10.5000"
                data-testid="new-currency-rate"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Ex: Si 1 USD = 9.40 MAD, entrez 9.40
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleAddCurrency} 
              disabled={saving}
              data-testid="save-new-currency-btn"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Creer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Component for individual currency row
function CurrencyRow({ currency, baseCurrency, isEditing, onEdit, onCancel, onSave, onDelete, saving }) {
  const [editData, setEditData] = useState({
    rate: currency.rate?.toString() || '',
    symbol: currency.symbol || '',
    label: currency.label || ''
  });

  const handleSave = () => {
    onSave({
      rate: parseFloat(editData.rate),
      symbol: editData.symbol,
      label: editData.label
    });
  };

  if (isEditing) {
    return (
      <div className="p-4 border-2 border-blue-300 rounded-lg bg-blue-50/50" data-testid={`currency-row-${currency.code}-editing`}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label className="text-xs">Code</Label>
            <Input value={currency.code} disabled className="font-mono bg-gray-100" />
          </div>
          <div>
            <Label className="text-xs">Libelle</Label>
            <Input
              value={editData.label}
              onChange={(e) => setEditData({ ...editData, label: e.target.value })}
              data-testid={`edit-label-${currency.code}`}
            />
          </div>
          <div>
            <Label className="text-xs">Symbole</Label>
            <Input
              value={editData.symbol}
              onChange={(e) => setEditData({ ...editData, symbol: e.target.value })}
              maxLength={3}
              data-testid={`edit-symbol-${currency.code}`}
            />
          </div>
          <div>
            <Label className="text-xs">Taux (= X {baseCurrency})</Label>
            <Input
              type="number"
              step="0.0001"
              value={editData.rate}
              onChange={(e) => setEditData({ ...editData, rate: e.target.value })}
              data-testid={`edit-rate-${currency.code}`}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
            <X className="w-4 h-4 mr-1" />
            Annuler
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid={`save-currency-${currency.code}`}>
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Enregistrer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
      data-testid={`currency-row-${currency.code}`}
    >
      <div className="flex items-center gap-4">
        <span className="text-2xl w-10 text-center">{currency.symbol}</span>
        <div>
          <p className="font-medium">{currency.label}</p>
          <p className="text-sm text-muted-foreground font-mono">{currency.code}</p>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="text-right">
          <p className="font-semibold text-lg">
            1 {currency.code} = <span className="text-primary">{safeFixed(currency.rate, 4, '-')}</span> {baseCurrency}
          </p>
          {currency.updatedAt && (
            <p className="text-xs text-muted-foreground">
              MAJ: {new Date(currency.updatedAt).toLocaleDateString('fr-FR')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onEdit}
            data-testid={`edit-currency-${currency.code}`}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onDelete}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
            data-testid={`delete-currency-${currency.code}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
